import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  ArrowRight, ShieldCheck, CalendarClock, Gauge, Layers, Bell, Wallet,
  Tv, CreditCard, Package, Receipt, RefreshCw, Users, Check, Lock, Ban,
} from "lucide-react";
import Seo from "../../components/Seo";
import SafeToSpendWidget from "../../components/marketing/SafeToSpendWidget";

const TRUST_BADGES = [
  { icon: Lock, title: "Read-Only Engine", sub: "Zero capability to move or transfer funds", testid: "trust-badge-read-only" },
  { icon: ShieldCheck, title: "No Credentials Needed", sub: "No bank passwords or logins required", testid: "trust-badge-no-credentials" },
  { icon: Ban, title: "Ad-Free Utility", sub: "Your data is private and never monetized", testid: "trust-badge-ad-free" },
];

function TrustBadges() {
  return (
    <div className="mt-8 grid sm:grid-cols-3 gap-2.5" data-testid="trust-badge-bar">
      {TRUST_BADGES.map((b) => (
        <div key={b.testid} className="flex items-start gap-2.5 rounded-xl border border-border bg-secondary/50 px-3.5 py-3" data-testid={b.testid}>
          <span className="h-8 w-8 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-card-sm">
            <b.icon size={15} strokeWidth={1.5} className="text-mint" />
          </span>
          <div>
            <p className="text-sm font-medium text-navy leading-tight">{b.title}</p>
            <p className="text-[11px] text-slatey leading-snug mt-0.5">{b.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const INTEL = [
  { icon: Wallet, title: "Committed This Month", desc: "The part of your income already spoken for, before you spend a rupee." },
  { icon: Gauge, title: "Commitment Ratio", desc: "How much of your income is committed, shown as a calm, clear figure." },
  { icon: ShieldCheck, title: "Safe to Spend", desc: "An estimate of what remains after commitments and your protected reserve." },
  { icon: CalendarClock, title: "Upcoming Commitments", desc: "What's due next, in order, with days remaining and payment source." },
  { icon: Layers, title: "Commitment Calendar", desc: "A visual timeline through your next income date and the current month." },
  { icon: Bell, title: "Review & Renewal Alerts", desc: "Gentle nudges before renewals and deductions — never alarming." },
];

const CATS = [
  { icon: Tv, label: "Subscriptions" }, { icon: CreditCard, label: "EMIs" },
  { icon: Package, label: "BNPL" }, { icon: Receipt, label: "Recurring bills" },
  { icon: RefreshCw, label: "Autopays" }, { icon: Users, label: "Shared commitments" },
];

const FRAGMENTS = ["Subscriptions", "EMIs", "BNPL", "Autopays", "Bills", "Shared plans", "Bank mandates"];

export default function Home() {
  const nav = useNavigate();
  const scrollToWidget = () => document.getElementById("safe-to-spend-widget")?.scrollIntoView({ behavior: "smooth", block: "center" });
  return (
    <div className="overflow-x-hidden">
      <Seo title={null} description="COMMITMENTT is your Income Shield — see your Commitment Position and Safe-to-Spend Visibility before you spend. Subscriptions, EMIs, BNPL, bills and autopays in one calm view." />
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-12 md:pt-16 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
          <div className="animate-fade-up">
            <span className="inline-block text-xs tracking-[0.2em] uppercase font-semibold text-mint mb-5">Your Income Shield</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06] text-navy">
              Know exactly what your income is already <span className="text-mint">committed</span> to.
            </h1>
            <p className="text-base md:text-lg text-slatey mt-6 leading-relaxed max-w-lg">
              Subscriptions, EMIs, BNPL dues, bills and autopays quietly claim your income before you spend it. COMMITMENTT is a forward-looking Income Shield — see your Commitment Position and Safe-to-Spend Visibility at a glance.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button size="lg" className="rounded-full bg-navy hover:bg-navy-light text-white h-12 px-7" onClick={scrollToWidget} data-testid="hero-calculate-btn">
                Calculate Your Safe-to-Spend <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button size="lg" variant="secondary" className="rounded-full h-12 px-7 bg-secondary text-ink hover:bg-border" onClick={() => nav("/register")} data-testid="hero-signup-btn">
                Sign Up Free
              </Button>
            </div>
            <TrustBadges />
          </div>
          <div className="lg:pl-4 animate-fade-up">
            <SafeToSpendWidget />
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink max-w-2xl leading-tight">
            Your bank balance is not the same as your available money.
          </h2>
          <p className="text-slatey mt-5 max-w-2xl leading-relaxed">
            Modern income is fragmented across many recurring obligations that leave your account automatically.
            Most finance apps tell you what you already spent. COMMITMENTT tells you what you have already committed to pay.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-8">
            {FRAGMENTS.map((f) => (
              <span key={f} className="text-sm px-4 py-2 rounded-full bg-secondary text-slatey">{f}</span>
            ))}
          </div>
          <div className="mt-12 rounded-2xl bg-navy text-white p-8 md:p-12">
            <p className="font-heading text-2xl md:text-3xl font-light leading-snug max-w-3xl">
              The world has moved from purchases to commitments. Personal finance has not.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">How it works</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink mt-3 mb-10">Four calm steps to clarity</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            "Add your income cycle",
            "Add or identify recurring commitments",
            "Review your monthly commitment position",
            "Act before renewals and deductions",
          ].map((s, i) => (
            <Card key={i} className="rounded-2xl border-border shadow-card-sm p-6 h-full">
              <div className="h-10 w-10 rounded-full bg-mint-light text-mint flex items-center justify-center font-heading font-semibold">{i + 1}</div>
              <p className="text-ink font-medium mt-4">{s}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Core intelligence */}
      <section className="bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Core intelligence</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink mt-3 mb-10">Everything your recurring money is doing</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INTEL.map((it) => (
              <Card key={it.title} className="rounded-2xl border-border shadow-card-sm p-6 h-full hover:shadow-card-md transition-shadow">
                <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center">
                  <it.icon size={20} strokeWidth={1.5} className="text-navy" />
                </div>
                <h3 className="font-heading text-lg font-medium text-ink mt-4">{it.title}</h3>
                <p className="text-sm text-slatey mt-2 leading-relaxed">{it.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24">
        <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink mb-10">One view for every commitment</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATS.map((c) => (
            <Card key={c.label} className="rounded-2xl border-border shadow-card-sm p-5 flex flex-col items-center text-center gap-3 hover:-translate-y-0.5 transition-transform">
              <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center">
                <c.icon size={20} strokeWidth={1.5} className="text-navy" />
              </div>
              <p className="text-sm font-medium text-ink">{c.label}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Privacy */}
      <section className="bg-white border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink leading-tight">
              Your commitments are personal. Your data stays yours.
            </h2>
            <p className="text-slatey mt-4 leading-relaxed">
              A privacy-first Income Shield: no advertising, no sale of financial behaviour, no bank credentials — and you can clear or delete everything anytime with one click.
            </p>
          </div>
          <ul className="space-y-3">
            {[
              "You own your data, always",
              "No advertising, ever",
              "No sale of financial behaviour to data brokers",
              "No bank credentials or logins ever required",
              "Clear or delete all your data anytime — one click",
              "Uploaded information is used only to generate the commitment record you asked for",
            ].map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full bg-mint-light text-mint flex items-center justify-center shrink-0 mt-0.5"><Check size={14} /></span>
                <span className="text-slatey">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* A note from the Founder */}
      <section className="bg-navy" data-testid="founder-message">
        <div className="max-w-3xl mx-auto px-5 py-20 md:py-28">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-mint">A note from the Founder</span>
          <div className="mt-6 space-y-5 text-white/80 leading-relaxed text-base md:text-lg font-light">
            <p>
              I built COMMITMENTT around a simple question: how much of the money we earn is actually ours to freely spend?
            </p>
            <p>
              Every month, a part of our income is already spoken for — EMIs, rent, subscriptions, insurance, bills, loans and other commitments. Yet most of us understand the real impact only when those payments become due.
            </p>
            <p>
              I wanted to build something that helps us see those commitments together, understand what is coming next, and make better-informed decisions before taking on something new.
            </p>
            <p>
              COMMITMENTT is still in Early Access, and that is why your genuine usage matters to me.
            </p>
            <p>
              I don't just need you to explore the product. I need you to use it the way you would naturally use it. Add your commitments, try the features, come back to it, and tell us where you felt confused, what didn't work, what felt unnecessary, and what you wish COMMITMENTT could do better.
            </p>
            <p>
              Your feedback at this stage will directly influence what we build next.
            </p>
            <p>
              My goal is simple: to make COMMITMENTT useful enough that anyone — even someone with no financial knowledge — can understand what they've committed to, plan what comes next, and stay ahead.
            </p>
            <p>
              Thank you for being one of the early people helping us build it.
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="font-heading text-lg text-white">Aleem Uddin</p>
            <p className="text-sm text-white/50">Founder, COMMITMENTT</p>
          </div>

          {/* Feedback CTA */}
          <div className="mt-10 rounded-2xl bg-white/[0.06] border border-white/10 p-6" data-testid="founder-feedback-cta">
            <p className="font-heading text-xl text-white">Share your feedback</p>
            <p className="text-sm text-white/70 mt-1.5 leading-relaxed max-w-lg">
              Found something confusing, useful, unnecessary, or missing? I genuinely want to hear it.
            </p>
            <Button className="rounded-full mt-5 bg-mint text-white hover:bg-mint/90 h-11 px-6" onClick={() => nav("/contact?category=feedback")} data-testid="founder-share-feedback-btn">
              Share Feedback <ArrowRight size={18} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Sign-up CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20 text-center">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-mint">Get started</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-medium text-ink mt-3 max-w-2xl mx-auto">
          See what your salary is already committed to.
        </h2>
        <p className="text-slatey mt-4 max-w-xl mx-auto">
          Create your free account in seconds — no waiting, no approval. Add your income and commitments and get your clarity today.
        </p>
        <Button size="lg" className="rounded-full mt-8 bg-navy text-white hover:bg-navy-light h-12 px-8" onClick={() => nav("/register")} data-testid="cta-trial-btn">
          Create Your Account <ArrowRight size={18} className="ml-1" />
        </Button>
        <p className="text-sm text-muted-foreground mt-6">
          Want a look first? <Link to="/demo" className="text-mint underline underline-offset-4">View the demo</Link>
        </p>
      </section>
    </div>
  );
}
